import { IApiClientResponse, isResponseError, TResponse } from 'models/apiClient';
import { apiClient } from 'utils/apiClient';
import { EActionTypes } from 'state/actions/auth';
import {
  IAuthBodyRequest,
  IAuthResponse,
  IDecodedAccessToken,
  IForgotPasswordRecaptcha,
  IRefreshAuthTokenRequest,
  IRefreshAuthTokenResponse,
  ISetPasswordBodyRequest,
  IVerifyAuthCodeBodyRequest,
} from 'models/auth';
import { ApiEndpoints } from 'models/apiEndpoints';
import { handleRestApiException } from 'utils/handleRestApiException';
import { EAlertVariants } from 'components/atoms/Alert';
import jwt_decode from 'jwt-decode';

export const FC_LICENSING_AUTH_ACCESS_TOKEN = 'FC_LICENSING_AUTH_ACCESS_TOKEN';
export const FC_LICENSING_AUTH_REFRESH_TOKEN = 'FC_LICENSING_AUTH_REFRESH_TOKEN';
export const FC_LICENSING_AUTH_TYPE_ACCOUNT = 'FC_LICENSING_AUTH_TYPE_ACCOUNT';

const loginRequest = async (body: IAuthBodyRequest): Promise<IApiClientResponse> => {
  try {
    await apiClient.post<TResponse<IAuthResponse>>(ApiEndpoints.LOG_IN(), body, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return {
      success: true,
      data: null,
    };
  } catch (e: any) {
    return {
      success: false,
      data: null,
      responseError: handleRestApiException(e),
    };
  }
};

export const getUser = async (dispatch: any) => {
  const authAccessToken: string | null = localStorage.getItem(FC_LICENSING_AUTH_ACCESS_TOKEN);
  if (authAccessToken !== null) {
    const {
      jti,
      sub,
      'First name': firstName,
      'Last name': lastName,
    }: IDecodedAccessToken = jwt_decode(authAccessToken);

    dispatch({
      type: EActionTypes.CHANGE_USER,
      payload: {
        userId: jti,
        email: sub,
        firstName,
        lastName,
      },
    });
  }
};

export const logIn = async (
  dispatch: any,
  body: IAuthBodyRequest,
  successCallback: (data: any) => void,
): Promise<TResponse<IAuthResponse>> => {
  dispatch({
    type: EActionTypes.LOG_IN,
  });

  const res = await loginRequest(body);
  try {
    if (res.success) {
      successCallback(res.success);
    } else {
      dispatch({
        type: EActionTypes.LOG_IN_FAILURE,
        payload: { authMessage: { text: res.responseError, variant: EAlertVariants.error } },
      });
      successCallback(false);
    }
  } catch (e) {
    console.error(e);
  }

  return res.data;
};

const setPasswordRequest = async (
  body: ISetPasswordBodyRequest,
): Promise<IApiClientResponse> => {
  const { accessToken, ...data } = body;
  try {
    await apiClient.patch<TResponse<IAuthResponse>>(ApiEndpoints.SET_PASSWORD(), data, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return {
      success: true,
      data: null,
    };
  } catch (e: any) {
    return {
      success: false,
      data: e?.response?.data,
      responseError: handleRestApiException(e),
    };
  }
};

export const setPassword = async (
  dispatch: any,
  body: ISetPasswordBodyRequest,
  successCallback: (data: any) => void,
): Promise<TResponse<IAuthResponse>> => {
  dispatch({
    type: EActionTypes.LOG_IN,
  });

  const res = await setPasswordRequest(body);
  try {
    if (res.success) {
      localStorage.removeItem(FC_LICENSING_AUTH_ACCESS_TOKEN);
      localStorage.removeItem(FC_LICENSING_AUTH_REFRESH_TOKEN);
      dispatch({
        type: EActionTypes.SET_PASSWORD_SUCCESS,
      });
      successCallback(res.success);
    } else {
      dispatch({
        type: EActionTypes.SET_PASSWORD_FAILURE,
        payload: { authMessage: { text: res.data?.message, variant: EAlertVariants.error } },
      });
    }
  } catch (e) {
    console.error(e);
  }

  return res.data;
};

const loginOutRequest = async (): Promise<IApiClientResponse> => ({
  success: true,
  data: true,
});
// try {
//   const { data } = await apiClient.post<TResponse<null>>(ApiEndpoints.LOG_OUT());
//   return {
//     success: true,
//     data,
//   };
// } catch (e: any) {
//   return {
//     success: false,
//     data: null,
//     responseError: handleRestApiException(e),
//   };
// }
export const logOut = async (
  dispatch: any,
  successCallback: () => void,
): Promise<TResponse<null>> => {
  const res = await loginOutRequest();
  try {
    if (res.success) {
      localStorage.removeItem(FC_LICENSING_AUTH_ACCESS_TOKEN);
      localStorage.removeItem(FC_LICENSING_AUTH_REFRESH_TOKEN);
      successCallback();
      dispatch({
        type: EActionTypes.LOG_OUT_SUCCESS,
        payload: {
          authMessage: {
            text: 'You were successfully logged out.',
            variant: EAlertVariants.success,
          },
        },
      });
    } else {
      dispatch({
        type: EActionTypes.LOG_OUT_FAILURE,
      });
    }
  } catch (e) {
    console.error(e);
  }
  return res.data;
};

export const changeAccessToken = async (dispatch: any, accessToken: string | null) => {
  dispatch({
    type: EActionTypes.CHANGE_ACCESS_TOKEN,
    payload: { accessToken },
  });
};

export const setIsAuthenticated = async (dispatch: any, isAuthenticated: boolean | null) => {
  dispatch({
    type: EActionTypes.IS_AUTHENTICATED,
    payload: { isAuthenticated },
  });
};

const verifyAuthCodeRequest = async (
  body: IVerifyAuthCodeBodyRequest,
): Promise<IApiClientResponse> => {
  try {
    const { code, ...bodyData } = body;
    const { data } = await apiClient.post<TResponse<IAuthResponse>>(
      ApiEndpoints.VERIFY_AUTH_CODE(code),
      bodyData,
    );
    return {
      success: true,
      data,
    };
  } catch (e: any) {
    return {
      success: false,
      data: null,
      responseError: handleRestApiException(e),
    };
  }
};

export const verifyAuthCode = async (
  dispatch: any,
  body: IVerifyAuthCodeBodyRequest,
  successCallback: (isFirstLogin: boolean) => void,
  failureCallback: (error: any) => void,
): Promise<TResponse<IAuthResponse>> => {
  dispatch({
    type: EActionTypes.LOG_IN,
  });

  const res = await verifyAuthCodeRequest(body);

  try {
    if (res.success) {
      const { accessToken, refreshToken, lastLoginDate } = res.data;
      const isFirstLogin = !lastLoginDate;
      dispatch({
        type: EActionTypes.LOG_IN_SUCCESS,
        payload: { accessToken, refreshToken, isFirstLogin },
      });
      if (!isFirstLogin) {
        localStorage.setItem(FC_LICENSING_AUTH_ACCESS_TOKEN, accessToken);
        localStorage.setItem(FC_LICENSING_AUTH_REFRESH_TOKEN, refreshToken);
        localStorage.setItem(FC_LICENSING_AUTH_TYPE_ACCOUNT, body.type ?? '');
      }
      successCallback(isFirstLogin);
    } else {
      dispatch({
        type: EActionTypes.CLEAR_AUTH_ERRORS,
      });
      dispatch({
        type: EActionTypes.LOG_IN_FAILURE,
      });
      failureCallback(res.responseError);
    }
  } catch (e) {
    console.error(e);
  }
  return res.data;
};

export const clearAuthErrors = async (dispatch: any) => {
  dispatch({
    type: EActionTypes.CLEAR_AUTH_ERRORS,
  });
};

export const refreshAuthToken = async (refreshToken: IRefreshAuthTokenRequest) => {
  const { data } = await apiClient.post<TResponse<IRefreshAuthTokenResponse>>(
    ApiEndpoints.REFRESH_AUTH_TOKEN(),
    refreshToken,
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};

export const changeForgotPasswordRecaptcha = async (token: string | null) => {
  const { data } = await apiClient.get<TResponse<IForgotPasswordRecaptcha>>(
    ApiEndpoints.CHANGE_FORGOT_PASSWORD_RECAPTCHA(`?gRecaptchaResponse=${token}`),
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};
